/**
 * useLiveTestingData Hook
 *
 * V2 REST API를 통해 선택된 QuestionTree의 질문 데이터를 로드
 */

import { useMemo } from 'react';
import { useQuestionControllerFindByTreeId } from '../../../../api/generated/questions/questions';
import { LiveTestingQuestion } from './types';

interface UseLiveTestingDataProps {
  /** 선택된 QuestionTree ID */
  treeId?: number | null;
  /** 현재 locale (번역 언어) */
  locale?: string;
  /** 비활성 질문 필터링 (환자 플로우에서 true) */
  enabledOnly?: boolean;
}

interface UseLiveTestingDataReturn {
  /** 질문 목록 */
  questions: LiveTestingQuestion[];
  /** 로딩 중 여부 */
  isLoading: boolean;
  /** 에러 발생 여부 */
  isError: boolean;
  /** 에러 객체 */
  error: Error | null;
  /** 질문 개수 */
  questionCount: number;
}

/**
 * Live Testing용 질문 데이터 로드 훅
 *
 * V2 API의 useQuestionControllerFindByTreeId를 호출하여
 * 선택된 QuestionTree의 질문 목록을 가져옴
 *
 * @example
 * ```tsx
 * const { questions, isLoading, isError } = useLiveTestingData({
 *   treeId: selectedTreeId,
 *   locale: currentLocale,
 * });
 * ```
 */
export function useLiveTestingData({
  treeId,
  locale = 'english',
  enabledOnly = false,
}: UseLiveTestingDataProps): UseLiveTestingDataReturn {
  // V2 API 호출
  const {
    data: questionsData,
    isLoading,
    isError,
    error,
  } = useQuestionControllerFindByTreeId(
    treeId ?? 0,
    { locale, ...(enabledOnly ? { enabledOnly: 'true' } : {}) } as any,
    {
      query: {
        enabled: !!treeId && treeId > 0,
      },
    }
  );

  // 질문 목록 메모이제이션
  const questions = useMemo<LiveTestingQuestion[]>(() => {
    if (!questionsData) return [];
    // API가 이미 entryQuestionKeys 순서로 정렬된 데이터 반환
    return questionsData;
  }, [questionsData]);

  // 질문 개수
  const questionCount = questions.length;

  return {
    questions,
    isLoading,
    isError,
    error: error as Error | null,
    questionCount,
  };
}
